import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { Button } from './ui/index.ts'
import { vaultAttentionSources } from '../lib/attention-sources.ts'
import {
  notificationPermission,
  requestNotificationPermission,
} from '../lib/notify.ts'
import type { NotifyPermission } from '../lib/notify.ts'
import { useAttentionWatcher } from '../lib/use-attention-watcher.ts'
import { loadVaultViewer } from '../lib/vault-viewer.ts'

/**
 * The header's bell: the in-app half of D11.
 *
 * While the tab is VISIBLE this is the whole notification surface — a
 * count, and nothing else. No toast, no sound, no modal. The watcher
 * fires an OS notification only while the tab is hidden, so an operator
 * sitting in front of the console is never interrupted by it; they
 * glance at a number.
 *
 * THE BELL IS ALSO THE ONLY PLACE PERMISSION IS EVER ASKED FOR. Browsers
 * now refuse a `Notification.requestPermission()` that did not come from
 * a user gesture, and a refused request is remembered — asking on load
 * does not merely annoy, it permanently denies. So the prompt hangs off
 * this click and nothing else in the app may call that function.
 *
 * It renders in the shell, not on a vault page, because the entire point
 * is to reach someone who is looking at something else. It is hidden for
 * a tenant without the Vault product: both sources are vault things, so
 * for those tenants it would be a permanent zero.
 */

export interface NotificationBellProps {
  /** Whether this tenant has the Vault product. `false` renders nothing
   * — see above. Comes from the shell, which already has the answer. */
  hasVault: boolean
}

/** What the bell knows about where it is pointed. `null` until the
 * lookup lands, and after a failed one — the sources handle `''` as "no
 * root, nothing to poll", so the watcher runs harmlessly either way. */
interface Viewer {
  rootId: string
  principalId: string | null
}

export function NotificationBell({
  hasVault,
}: NotificationBellProps): ReactElement | null {
  const [viewer, setViewer] = useState<Viewer | null>(null)
  // Read once on mount rather than on every render: `Notification.
  // permission` is a live global, but it only changes as a result of the
  // click below (or a browser-chrome action that will not re-render us
  // anyway), so polling it would be work for nothing.
  const [permission, setPermission] = useState<NotifyPermission>('default')

  useEffect(() => {
    setPermission(notificationPermission())
  }, [])

  useEffect(() => {
    if (!hasVault) return
    let isCancelled = false
    loadVaultViewer()
      .then((resolved) => {
        if (!isCancelled) setViewer(resolved)
      })
      .catch(() => {
        // The tenant root could not be resolved — a /hierarchy blip, or
        // a signed-out shell on /login. Not reported: the bell is an
        // affordance, and an error banner in the chrome because a
        // background lookup failed would be worse than a bell that sits
        // at zero until the next page load retries it.
        if (!isCancelled) setViewer(null)
      })
    return () => {
      isCancelled = true
    }
  }, [hasVault])

  // Built fresh every render, which is exactly what the watcher's ref
  // indirection is designed for. An empty root makes both sources no-ops.
  const { count, failedSourceIds } = useAttentionWatcher(
    vaultAttentionSources(viewer?.rootId ?? '', viewer?.principalId ?? null),
  )

  if (!hasVault) return null

  async function handleClick(): Promise<void> {
    // The one call site. `default` only: re-asking after `granted` is a
    // no-op, and after `denied` the browser will not show the prompt
    // again, so pretending otherwise would be theatre.
    if (permission !== 'default') return
    setPermission(await requestNotificationPermission())
  }

  const label = bellLabel(count, permission, failedSourceIds)

  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={label}
      title={label}
      onClick={() => void handleClick()}
    >
      <BellIcon />
      {count > 0 && (
        // `aria-hidden` because the count is already in the button's
        // accessible name, and a screen reader reading "12" after "12
        // things need your attention" is noise.
        <span
          aria-hidden="true"
          className="min-w-4 rounded-full bg-status-danger px-1 text-[10px] leading-4 font-medium text-status-danger-fg"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Button>
  )
}

/**
 * The bell's accessible name, which carries everything the badge cannot.
 *
 * Three facts get folded in, in priority order: how much is waiting,
 * whether desktop notifications are on, and whether a source could not
 * be reached. The last one matters more than it looks — "no approvals
 * are pending" and "I could not ask about approvals" render as the same
 * silent bell, and an operator trusting a silent watcher should be able
 * to find out which one they have.
 */
function bellLabel(
  count: number,
  permission: NotifyPermission,
  failedSourceIds: readonly string[],
): string {
  const waiting =
    count === 0
      ? 'Nothing needs your attention'
      : `${String(count)} ${count === 1 ? 'item needs' : 'items need'} your attention`
  const notifications =
    permission === 'granted'
      ? 'desktop notifications are on'
      : permission === 'denied'
        ? 'desktop notifications are blocked by this browser'
        : permission === 'unsupported'
          ? 'this browser has no desktop notifications'
          : 'click to turn on desktop notifications'
  const failed =
    failedSourceIds.length === 0
      ? ''
      : ` — could not check: ${failedSourceIds.join(', ')}`
  return `${waiting}; ${notifications}${failed}`
}

/* `currentColor` stroke on a 16px box, same contract as `ThemeToggle`'s
   glyphs: the icon inherits the Button's token colour rather than
   carrying one of its own. `aria-hidden` because the button is named
   above, and an SVG that also announces itself reads the control twice. */
function BellIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6.6a4 4 0 0 1 8 0c0 2.4.6 3.6 1.2 4.3.3.3.1.9-.4.9H3.2c-.5 0-.7-.6-.4-.9C3.4 10.2 4 9 4 6.6Z" />
      <path d="M6.6 13.6a1.6 1.6 0 0 0 2.8 0" />
    </svg>
  )
}
